<template>
    <div class="EvaluationError">
        <template v-if="showErrorMessage && !hasException">
            <FontAwesomeIcon :icon="faExclamationTriangle" />
            <div class="EvaluationError__messages">
                <div
                    v-for="(message, index) in evaluationError.messages"
                    :key="index"
                    class="EvaluationError__message"
                >
                    <b>{{ message.coverageCriteriaGroupName }} - </b>
                    <span>{{ message.message }}</span>
                </div>
            </div>
        </template>
        <ExceptionBadge
            v-if="showExceptionBadge && hasException"
            class="EvaluationError__exceptionBadge"
            :required-message="evaluationError.messages[0].message"
            :applied-to="exceptionAppliedTo"
            :exception-type="statusOverride[0] && statusOverride[0].type"
            :until="exceptionUntil"
            :note="statusOverride[0] && statusOverride[0].note"
            :disable-popover="false"
            :label="exceptionBadgeLabel"
        />
        <RbacDisplayGuard
            :features="[RbacFeature.entityDetailsAddRemoveException]"
        >
            <Expandable :menu-config="{ positionX: 'end', teleport: true }">
                <template #trigger="{ toggle}">
                    <NewButton variant="flat" size="icon-sm" color="secondary">
                        <EidIcon
                            :icon-src="faEllipsisVertical"
                            class="EvaluationError__actionsButton"
                            @click="toggle"
                        />
                    </NewButton>
                </template>
                <template #default="{ close }">
                    <ExpandableItem
                        v-if="statusOverride.length > 0"
                        label="Remove Exception"
                        :disabled="hasHigherLevelException"
                        @click="$emit('removeExceptions', exceptionIds); close();"
                    />
                    <ExpandableItem
                        v-else
                        label="Grant Exception"
                        @click="$emit('grantExceptionToCriterion', evaluationError.criterionId); close();"
                    >
                        <div class="flex flex-col gap-1">
                            <div>Grant Exception</div>
                            <div v-if="showExceptionSubLabel" class="text-xs text-eidLightGray">
                                This non-compliant criteria will be waived as compliant. This can be undone.
                            </div>
                        </div>
                    </ExpandableItem>
                    <ExpandableItem
                        v-if="showGoToCriterion && statusOverride.length < 1"
                        label="View criteria"
                        @click="$emit('goToCriterion', evaluationError.coverageCriteriaGroupId); close();"
                    />
                </template>
            </Expandable>
            <template #displayIfNoAccess>
                <Expandable v-if="showGoToCriterion" :menu-config="{ positionX: 'end', teleport: true }">
                    <template #trigger="{ toggle}">
                        <NewButton variant="flat" size="icon-sm" color="secondary">
                            <EidIcon
                                :icon-src="faEllipsisVertical"
                                class="EvaluationError__actionsButton"
                                @click="toggle()"
                            />
                        </NewButton>
                    </template>
                    <template #default="{ close }">
                        <ExpandableItem
                            v-if="showGoToCriterion"
                            label="View criteria"
                            @click="$emit('goToCriterion', evaluationError.coverageCriteriaGroupId); close();"
                        />
                    </template>
                </Expandable>
            </template>
        </RbacDisplayGuard>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faEllipsisH, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import RbacDisplayGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import {
        ExceptionLevel,
        ExceptionType,
        ComplianceStatusOverride,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import Expandable from '@/components/expandable/Expandable.vue';
    import ExpandableItem from '@/components/expandable-item/ExpandableItem.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
    import ExceptionBadge from '@/modules/entity-details/components/ExceptionBadge/ExceptionBadge.vue';

    export default defineComponent({
        name: 'EvaluationError',
        components: {
            FontAwesomeIcon,
            EidIcon,
            ExceptionBadge,
            Expandable,
            ExpandableItem,
            RbacDisplayGuard,
            NewButton,
        },
        props: {
            evaluationError: {
                type: Object as PropType<FieldEvaluationResultError>,
                required: true,
            },
            showErrorMessage: {
                type: Boolean as PropType<Boolean>,
                default: true,
            },
            showExceptionBadge: {
                type: Boolean as PropType<Boolean>,
                default: true,
            },
            showGoToCriterion: {
                type: Boolean as PropType<Boolean>,
                default: true,
            },
            exceptionBadgeLabel: {
                type: String as PropType<string>,
                default: undefined,
            },
            showExceptionSubLabel: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
        },
        emits: [ 'goToCriterion', 'grantExceptionToCriterion', 'removeExceptions' ],
        data() {
            return {
                faExclamationTriangle,
                faEllipsisVertical,
                RbacFeature,
            };
        },
        computed: {
            hasException(): boolean {
                return this.evaluationError.complianceStatusMetadata.length > 0;
            },
            statusOverride(): ComplianceStatusOverride[] {
                return this.evaluationError.complianceStatusMetadata
                    .reduce((acc, metadata) => {
                        if (metadata.statusOverride) {
                            acc.push(metadata.statusOverride);
                        }
                        return acc;
                    }, [] as ComplianceStatusOverride[]);
            },
            exceptionUntil(): string | null {
                if (this.statusOverride[0]?.type === ExceptionType.untilCoverageExpiration) {
                    return this.evaluationError.coverageDetailsBelongTo.coverage?.policy.expirationDate || null;
                } else {
                    return this.statusOverride[0]?.until || null;
                }
            },
            exceptionAppliedTo(): string {
                if (this.statusOverride.length === 0) {
                    return '';
                }
                switch (this.statusOverride[0].level) {
                    case ExceptionLevel.insured:
                        return 'Entity';
                    case ExceptionLevel.requirementInstance:
                        return 'Requirement';
                    default:
                        return this.evaluationError.criterionNames[0];
                }
            },
            exceptionIds(): string[] {
                return this.statusOverride.map((override) => override.id);
            },
            hasHigherLevelException(): boolean {
                return this.evaluationError.complianceStatusMetadata.some((metadata) => {
                    const exceptionLevel = metadata?.statusOverride?.level;
                    if (!exceptionLevel) {
                        return false;
                    }
                    return [
                        ExceptionLevel.requirementInstance,
                        ExceptionLevel.insured,
                    ].includes(exceptionLevel);
                });
            },
        },
    });
</script>
